import React from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { COMPANY, BANK } from './documentTypes';

/**
 * The printed document, on screen.
 *
 * The office quotes from an A4 sheet and reads the PDF back against it, so the
 * editor is laid out as that sheet rather than as a form: navy header band,
 * teal rule, the same section order, the same gold section markers. The colours
 * are the print palette (`doc-*` in the Tailwind config), not the app's working
 * blue — this is the customer's copy and it has to look like the customer's copy.
 *
 * Everything below is chrome only. No arithmetic and no state lives here.
 */

/** A field inside a party box: tiny uppercase label over a flat input. */
export const DOC_FIELD = 'h-7 border-0 bg-transparent px-1 text-sm shadow-none focus-visible:ring-1';

export function DocPage({ title, children, className }) {
  return (
    <div className={cn('mx-auto w-full max-w-[52rem] overflow-hidden rounded-lg border border-doc-rule bg-white shadow-card', className)}>
      <header className="flex flex-wrap items-start justify-between gap-3 bg-doc-navy px-5 py-4">
        <div className="min-w-0">
          <p className="font-heading text-2xl font-bold leading-none text-white">
            La<span className="text-doc-gold">b</span>elify
          </p>
          <p className="mt-0.5 text-[11px] font-semibold tracking-[0.12em] text-sky-300">PVT. LTD.</p>
          <div className="mt-2 space-y-0.5 text-[10px] leading-snug text-white/75">
            <p>{COMPANY.address}</p>
            <p>{COMPANY.phones}</p>
            <p>GSTIN: <span className="font-mono font-semibold text-white">{COMPANY.gstin}</span></p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-heading text-2xl font-extrabold uppercase tracking-[0.12em] text-doc-gold">{title}</p>
          <p className="text-[10px] tracking-[0.1em] text-white/45">{COMPANY.unit}</p>
        </div>
      </header>
      <div className="h-1 bg-doc-teal" />
      {children}
    </div>
  );
}

export function DocFooter({ note }) {
  return (
    <footer className="flex flex-wrap items-center justify-between gap-2 bg-doc-navy px-5 py-2">
      <p className="text-[9px] text-white/40">{note}</p>
      <p className="text-[9px] font-bold text-doc-gold">Labelify Pvt. Ltd. — GSTIN: {COMPANY.gstin}</p>
    </footer>
  );
}

/** The gold-marked section label the printed sheet uses above every block. */
export function SectionHeading({ children, className }) {
  return (
    <p className={cn('mb-1.5 border-l-[3px] border-doc-gold pl-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-doc-navy', className)}>
      {children}
    </p>
  );
}

const BOX_TONES = {
  navy: 'bg-doc-navy text-white',
  teal: 'bg-doc-teal text-white',
  die: 'bg-doc-die text-white',
  plain: 'bg-slate-100 text-doc-navy',
};

export function DocBox({ heading, tone = 'navy', children, className, actions }) {
  return (
    <section className={cn('overflow-hidden rounded border border-doc-rule', className)}>
      <div className={cn('flex items-center justify-between gap-2 px-2.5 py-1', BOX_TONES[tone])}>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.13em]">{heading}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function FieldLabel({ children, className }) {
  return (
    <span className={cn('block text-[9px] uppercase tracking-[0.1em] text-doc-muted', className)}>
      {children}
    </span>
  );
}

/** A labelled meta cell — quotation number, date, valid until. */
export function MetaField({ label, children }) {
  return (
    <div className="rounded border border-doc-rule px-2 py-1">
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

export function DocTextField({ label, value, onChange, placeholder, testId, mono, readOnly }) {
  return (
    <label className="block">
      <FieldLabel className="mt-1">{label}</FieldLabel>
      <Input
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        data-testid={testId}
        className={cn(DOC_FIELD, mono && 'font-mono font-semibold text-doc-navy')}
      />
    </label>
  );
}

export function BankDetails() {
  return (
    <DocBox heading="Bank Details" tone="teal">
      <dl className="grid grid-cols-2">
        {BANK.map(([label, value], i) => (
          <div
            key={label}
            className={cn(
              'border-b border-slate-100 px-2.5 py-1',
              i % 2 === 0 && 'border-r',
              i === BANK.length - 1 && 'col-span-2 border-r-0'
            )}
          >
            <dt className="text-[9px] uppercase tracking-[0.1em] text-doc-muted">{label}</dt>
            <dd className="text-[12px] font-semibold text-doc-navy">{value}</dd>
          </div>
        ))}
      </dl>
    </DocBox>
  );
}

/** The dashed "+ Add row" button the printed sheet's editor uses under a table. */
export function AddRowButton({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="rounded border border-dashed border-doc-navy px-2.5 py-1 text-[11px] font-semibold text-doc-navy transition-colors hover:bg-doc-navy hover:text-white"
    >
      {children}
    </button>
  );
}
